import { useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCircleQuestion, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import OrgSwitcher from './OrgSwitcher.js';
import BotSelector from './BotSelector.js';
import UserMenu from './UserMenu.js';
import InfoTooltip from './InfoTooltip.js';
import TestAgentButton from './TestAgentButton.js';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import { isOverviewPath, isAccountPath } from '../constants/overviewNav.js';
import './AppHeader.css';

// Crisp 1px vertical rule. Drawn as SVG with shape-rendering="crispEdges"
// so the browser snaps it to the device-pixel grid instead of anti-aliasing
// it — a plain 1px CSS box smears to ~2px when it lands on a fractional
// pixel (which the variable-width selectors push it onto). Colour comes
// from the `.app-header-divider` CSS `color` via fill="currentColor".
function HeaderDivider() {
  return (
    <svg
      className="app-header-divider"
      width="1"
      height="26"
      viewBox="0 0 1 26"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="1" height="26" fill="currentColor" shapeRendering="crispEdges" />
    </svg>
  );
}

// Horizontal hairline along the header's bottom edge — the seam against the
// sidebar. Same SVG crispEdges technique and shared colour/thickness as
// HeaderDivider, just laid out across (positioned by .app-header-seam CSS).
function HeaderSeam() {
  return (
    <svg
      className="app-header-seam"
      height="1"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="100%" height="1" fill="currentColor" shapeRendering="crispEdges" />
    </svg>
  );
}

// Top app bar (desktop): logo + workspace + bot selectors on the left;
// Help / Resources icons + the account menu on the right.
// The burger (mobile only) toggles the sidebar.
// On mobile (isMobile) the workspace/bot selectors and the Test Agent button
// move OUT of this header and INTO the sidebar (see AppSidebar) — the header
// then carries only the burger, logo, help, and the account menu.
function AppHeader({ onToggleSidebar, onLogout, showLogout, wixSite = null, isMobile = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { requestUnsavedAction } = useUnsavedChanges();
  const { selectedBot } = useSelectedBot();
  // The bot selector is per-agent, so it's hidden on the account-level
  // overview pages (Agents / Workspaces / Team members) where no single
  // agent is in context.
  //
  // Account pages (Account settings / Billing) have no level of their own:
  // they inherit the selector visibility from the page the user came from, so
  // opening one never adds or removes the bot selector. We cache the last
  // decision made on a real page and reuse it while on an account page.
  const isAccountPage = isAccountPath(location.pathname);
  const isOverview = isOverviewPath(location.pathname);
  const lastShowBotSelector = useRef(!isOverview);
  if (!isAccountPage) {
    lastShowBotSelector.current = !isOverview;
  }
  const showBotSelector = isAccountPage ? lastShowBotSelector.current : !isOverview;

  return (
    <header className="app-header">
      <div className="app-header-left">
        <button
          type="button"
          className="app-header-burger"
          onClick={onToggleSidebar}
          aria-label={t('header_toggle_menu')}
        >
          <FontAwesomeIcon icon={faBars} />
        </button>

        <a
          href="https://ultimo-bots.com"
          target="_blank"
          rel="noopener noreferrer"
          className="app-header-logo"
          title="Ultimo Bots"
        >
          <img src="/ultimo-bots-logo-white.png" alt="Ultimo Bots" />
        </a>

        {!isMobile && (
          <>
            <HeaderDivider />
            <OrgSwitcher variant="header" />
            {showBotSelector && (
              <>
                <HeaderDivider />
                <BotSelector variant="header" />
                {selectedBot && (
                  <>
                    <HeaderDivider />
                    <TestAgentButton variant="header" />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div className="app-header-right">
        <InfoTooltip text={t('help')} placement="bottom" compact>
          <button
            type="button"
            className="app-header-icon"
            aria-label={t('help')}
            onClick={() => requestUnsavedAction(() => navigate('/help'))}
          >
            <FontAwesomeIcon icon={faCircleQuestion} />
          </button>
        </InfoTooltip>

        {!isMobile && (
          <InfoTooltip text={t('header_resources')} placement="bottom" compact>
            <a
              className="app-header-icon"
              aria-label={t('header_resources')}
              href="https://docs.ultimo-bots.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faBookOpen} />
            </a>
          </InfoTooltip>
        )}

        <HeaderDivider />

        <UserMenu onLogout={onLogout} showLogout={showLogout} wixSite={wixSite} botInContext={showBotSelector} />
      </div>

      <HeaderSeam />
    </header>
  );
}

export default AppHeader;
